import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabaseClient, saveToSupabase, fetchFromSupabase } from '../config/supabase';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import toast from 'react-hot-toast';
import { monitoringService } from '../services/monitoringService';
import { loggingService } from '../services/loggingService';

export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  isSubscribed: boolean;
  role: 'user' | 'admin';
  createdAt: string;
}

interface AuthState {
  user: SupabaseUser | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<SupabaseUser | null>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  isLoading: boolean;
  token: string | null;
  isAuthenticated: boolean;
  
  // Akcje
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, name: string) => Promise<boolean>;
  logout: () => void;
  resetPassword: (email: string) => Promise<boolean>;
  updateUser: (userData: Partial<User>) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      loading: false,
      error: null,
      initialized: false,
      isLoading: false,
      token: null,
      isAuthenticated: false,

      signIn: async (email: string, password: string) => {
        try {
          set({ loading: true, error: null });
          monitoringService.trackUserBehavior('sign_in_attempt', { email });

          const { data, error } = await supabaseClient.auth.signInWithPassword({
            email,
            password,
          });

          if (error) throw error;

          set({
            user: data.user,
            session: data.session,
            initialized: true,
            loading: false,
          });

          toast.success('Witaj z powrotem!');
        } catch (error: any) {
          console.error('Sign in error:', error);
          loggingService.error('Sign in failed', error);
          toast.error('Błąd logowania. Sprawdź dane i spróbuj ponownie.');
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      signUp: async (email: string, password: string, fullName: string) => {
        set({ loading: true, error: null });
        try {
          const { data: signUpData, error: signUpError } = await supabaseClient.auth.signUp({
            email,
            password,
            options: {
              data: {
                full_name: fullName,
              },
            },
          });

          if (signUpError) {
            console.error('Sign up error:', signUpError);
            throw signUpError;
          }

          if (!signUpData.user) {
            throw new Error('Rejestracja nie powiodła się - brak danych użytkownika.');
          }

          console.log('User signed up:', signUpData.user.id);

          // --- NOWA LOGIKA: Obsługa konfliktu profilu (409) ---
          const { data: existingProfile, error: fetchProfileError } = await fetchFromSupabase<{ id: string }>({
            tableName: 'profiles',
            filters: { id: signUpData.user.id },
            single: true,
            useCache: false,
          });

          if (fetchProfileError && fetchProfileError.code !== 'PGRST116' && fetchProfileError.code !== '404') {
            console.error('Error checking for existing profile:', fetchProfileError);
          }

          if (existingProfile) {
            console.log('Profil dla użytkownika już istnieje, pomijam tworzenie.');
          } else {
            console.log('Profil nie istnieje, tworzenie nowego...');
            const { error: profileError } = await saveToSupabase({
              tableName: 'profiles',
              data: {
                id: signUpData.user.id,
                email: email,
                full_name: fullName,
              },
            });

            if (profileError) {
              console.error('Failed to create profile after sign up:', profileError);
              toast.error('Wystąpił błąd podczas tworzenia profilu.');
            } else {
              console.log('Profil utworzony pomyślnie.');
            }
          }
          // --- KONIEC NOWEJ LOGIKI ---

          set({ user: signUpData.user, session: signUpData.session, loading: false });
          toast.success('Rejestracja pomyślna! Sprawdź email, aby potwierdzić konto.');
          return signUpData.user;
        } catch (error: any) {
          console.error('Sign up process failed:', error);
          const errorMessage = error.message || 'Nieznany błąd rejestracji.';
          set({ error: errorMessage, loading: false });
          toast.error(`Błąd rejestracji: ${errorMessage}`);
          return null;
        }
      },

      signInWithGoogle: async () => {
        try {
          set({ loading: true, error: null });
          const { error } = await supabaseClient.auth.signInWithOAuth({
            provider: 'google',
            options: {
              redirectTo: `${window.location.origin}/auth/callback`,
              queryParams: {
                access_type: 'offline',
                prompt: 'consent',
              },
            },
          });

          if (error) throw error;
        } catch (error: any) {
          console.error('Google sign in error:', error);
          loggingService.error('Google sign in failed', error);
          toast.error('Błąd logowania przez Google. Spróbuj ponownie później.');
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      signOut: async () => {
        try {
          set({ loading: true, error: null });
          monitoringService.trackUserBehavior('sign_out_attempt');

          const { error } = await supabaseClient.auth.signOut();
          if (error) throw error;

          set({
            user: null,
            session: null,
            initialized: true,
            loading: false,
          });

          toast.success('Wylogowano pomyślnie');
        } catch (error: any) {
          console.error('Sign out error:', error);
          loggingService.error('Sign out failed', error);
          toast.error('Błąd wylogowania');
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      login: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null });
          monitoringService.trackEvent('AUTH_LOGIN_ATTEMPT', { email });
          // Symulacja opóźnienia zapytania
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          if (email === 'test@example.com' && password === 'password') {
            const mockUser: User = {
              id: '1',
              email: 'test@example.com',
              name: 'Test User',
              isSubscribed: true,
              role: 'user',
              createdAt: new Date().toISOString(),
            };
            
            const mockToken = 'mock_jwt_token';
            
            set({
              user: mockUser as any,
              token: mockToken,
              isAuthenticated: true,
              isLoading: false,
            });
            
            monitoringService.trackEvent('AUTH_LOGIN_SUCCESS', { userId: mockUser.id });
            return true;
          } else {
            set({
              error: 'Nieprawidłowy email lub hasło',
              isLoading: false,
              isAuthenticated: false,
            });
            
            monitoringService.trackEvent('AUTH_LOGIN_FAILURE', { reason: 'invalid_credentials' });
            return false;
          }
        } catch (error) {
          monitoringService.trackError(new Error('login_error'), { error });
          set({
            error: 'Wystąpił błąd podczas logowania. Spróbuj ponownie.',
            isLoading: false,
            isAuthenticated: false,
          });
          return false;
        }
      },

      register: async (email: string, password: string, name: string) => {
        try {
          set({ isLoading: true, error: null });
          monitoringService.trackEvent('AUTH_REGISTER_ATTEMPT', { email });
          // Symulacja opóźnienia zapytania
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          const mockUser: User = {
            id: Math.random().toString(36).substr(2, 9),
            email,
            name,
            isSubscribed: false,
            role: 'user',
            createdAt: new Date().toISOString(),
          };
          
          const mockToken = 'mock_jwt_token_for_new_user';
          
          set({
            user: mockUser as any,
            token: mockToken,
            isAuthenticated: true,
            isLoading: false,
          });
          
          monitoringService.trackEvent('AUTH_REGISTER_SUCCESS', { userId: mockUser.id });
          return true;
        } catch (error) {
          monitoringService.trackError(new Error('register_error'), { error });
          set({
            error: 'Wystąpił błąd podczas rejestracji. Spróbuj ponownie.',
            isLoading: false,
            isAuthenticated: false,
          });
          return false;
        }
      },

      logout: () => {
        monitoringService.trackEvent('AUTH_LOGOUT', { userId: get().user?.id });
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
      },

      resetPassword: async (email: string) => {
        try {
          set({ isLoading: true, error: null });
          monitoringService.trackEvent('AUTH_RESET_PASSWORD_ATTEMPT', { email });
          // Symulacja opóźnienia zapytania
          await new Promise(resolve => setTimeout(resolve, 1000));
          set({ isLoading: false });
          monitoringService.trackEvent('AUTH_RESET_PASSWORD_SUCCESS', { email });
          return true;
        } catch (error) {
          monitoringService.trackError(new Error('reset_password_error'), { error });
          set({
            error: 'Wystąpił błąd podczas resetowania hasła. Spróbuj ponownie.',
            isLoading: false,
          });
          return false;
        }
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (!currentUser) {
          set({ error: 'Nie jesteś zalogowany' });
          return;
        }
        monitoringService.trackEvent('AUTH_UPDATE_USER', { userId: currentUser.id });
        const updatedUser = { ...currentUser, ...userData };
        set({ user: updatedUser });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
